import React, { useCallback, useEffect, useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { TrendingUp, TrendingDown, DollarSign, Clock, Link2, ArrowRight, RefreshCw } from 'lucide-react';
import api from '../../services/api';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 }).format(n || 0);

export default function AccountsOverview() {
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [seeding, setSeeding] = useState(false);
  const location = useLocation();

  // Derive entity slug from URL path: /accounts/usa/... → "usa"
  const entitySlug = location.pathname.includes('/usa/') ? 'usa'
    : location.pathname.includes('/india/') ? 'india'
    : 'all';
  const entityLabel = { usa: 'Eyvo USA', india: 'Eyvo India', all: 'All Entities' }[entitySlug];

  const load = useCallback(() => {
    setLoading(true);
    api.get(`/api/accounts/reports/summary?entity=${entitySlug}`)
      .then(r => setSummary(r.data))
      .catch(() => setSummary(null))
      .finally(() => setLoading(false));
  }, [entitySlug]);

  useEffect(() => { load(); }, [load]);

  const seedCOA = async () => {
    setSeeding(true);
    try {
      await api.post('/api/accounts/chart/seed');
      alert('Chart of accounts seeded successfully!');
      load();
    } catch (e) {
      if (e.response?.data?.detail?.includes('already seeded')) {
        alert('Chart of accounts already seeded.');
      } else {
        alert(e.response?.data?.detail || 'Seed failed');
      }
    }
    setSeeding(false);
  };

  const KPICard = ({ label, value, sub, color, icon: Icon, trend }) => (
    <div className="bg-white border border-slate-200 rounded-lg p-5" data-testid={`kpi-${label.toLowerCase().replace(/\s/g,'-')}`}>
      <div className="flex items-start justify-between">
        <div>
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className={`text-2xl font-bold mt-1 font-mono ${color}`}>{value}</p>
          {sub && <p className="text-xs text-slate-400 mt-0.5">{sub}</p>}
        </div>
        <div className={`p-2 rounded-lg ${trend === 'up' ? 'bg-emerald-50' : trend === 'down' ? 'bg-red-50' : 'bg-slate-100'}`}>
          <Icon size={18} className={trend === 'up' ? 'text-emerald-600' : trend === 'down' ? 'text-red-500' : 'text-slate-500'} />
        </div>
      </div>
    </div>
  );

  return (
    <div className="p-6 fade-in" data-testid="accounts-overview-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Accounts</h1>
          {summary && <p className="text-sm text-slate-500 mt-0.5">{summary.month} · {entityLabel}</p>}
        </div>
        <div className="flex gap-2">
          <button onClick={seedCOA} disabled={seeding}
            className="px-3 py-2 text-xs border border-slate-200 rounded-md hover:bg-slate-50 font-medium text-slate-600 disabled:opacity-50"
            data-testid="seed-coa-btn">
            {seeding ? 'Seeding…' : 'Seed Chart of Accounts'}
          </button>
          <button onClick={load} className="p-2 border border-slate-200 rounded-md hover:bg-slate-50">
            <RefreshCw size={14} className="text-slate-500" />
          </button>
        </div>
      </div>

      {loading ? (
        <div className="grid grid-cols-4 gap-4 mb-6">
          {[...Array(4)].map((_, i) => <div key={i} className="h-28 bg-slate-100 rounded-lg animate-pulse" />)}
        </div>
      ) : summary ? (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <KPICard label="Revenue (MTD)" value={fmt(summary.month_revenue)} sub={summary.month}
            color="text-emerald-700" icon={TrendingUp} trend="up" />
          <KPICard label="Expenses (MTD)" value={fmt(summary.month_expenses)} sub={summary.month}
            color="text-red-600" icon={TrendingDown} trend="down" />
          <KPICard label="Net Income (MTD)" value={fmt(summary.month_net)} sub={summary.month}
            color={summary.month_net >= 0 ? 'text-emerald-700' : 'text-red-600'} icon={DollarSign}
            trend={summary.month_net >= 0 ? 'up' : 'down'} />
          <KPICard label="Pending Transactions" value={summary.pending_transactions}
            sub="Need categorization" color="text-amber-700" icon={Clock} />
        </div>
      ) : null}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* Bank Accounts */}
        <div className="lg:col-span-2 bg-white border border-slate-200 rounded-lg" data-testid="bank-accounts-panel">
          <div className="flex items-center justify-between px-5 py-3 border-b border-slate-200">
            <h2 className="text-sm font-semibold text-slate-900" style={{ fontFamily: 'Chivo, sans-serif' }}>Bank Accounts</h2>
            <Link to="/accounts/bank-connections" className="text-xs text-indigo-700 hover:text-indigo-900 flex items-center gap-1">
              <Link2 size={11} /> Manage
            </Link>
          </div>
          <div className="divide-y divide-slate-100">
            {loading ? [...Array(3)].map((_, i) => <div key={i} className="px-5 py-3 h-12 animate-pulse bg-slate-50" />) :
              (summary?.bank_accounts || []).length === 0 ? (
                <div className="px-5 py-8 text-center text-slate-400 text-sm">
                  <p>No bank accounts yet.</p>
                  <Link to="/accounts/bank-connections" className="text-indigo-700 hover:underline text-xs mt-1 inline-block">Connect a bank account</Link>
                </div>
              ) : (
                (summary?.bank_accounts || []).map(acc => (
                  <div key={acc.account_id} className="px-5 py-3 flex items-center justify-between">
                    <div>
                      <p className="text-sm font-medium text-slate-800">{acc.account_name}</p>
                      <p className="text-xs text-slate-400 font-mono">{acc.account_code}</p>
                    </div>
                    <span className={`font-mono font-semibold text-sm ${acc.balance >= 0 ? 'text-slate-900' : 'text-red-600'}`}>
                      {fmt(acc.balance)}
                    </span>
                  </div>
                ))
              )
            }
          </div>
        </div>

        {/* Quick Actions */}
        <div className="space-y-3">
          <div className="bg-white border border-slate-200 rounded-lg p-5">
            <h2 className="text-sm font-semibold text-slate-900 mb-4" style={{ fontFamily: 'Chivo, sans-serif' }}>Quick Actions</h2>
            <div className="space-y-2">
              {[
                { to: '/accounts/transactions', label: 'Review Transactions', desc: 'Categorize pending items' },
                { to: '/accounts/journal-entries/new', label: 'New Journal Entry', desc: 'Double-entry booking' },
                { to: '/accounts/pl-report', label: 'P&L Report', desc: 'Income vs Expenses' },
                { to: '/accounts/balance-sheet', label: 'Balance Sheet', desc: 'Assets, Liabilities, Equity' },
                { to: '/accounts/bank-connections', label: 'Sync Bank (Plaid)', desc: 'Import new transactions' },
              ].map(item => (
                <Link key={item.to} to={item.to}
                  className="flex items-center justify-between p-3 rounded-lg hover:bg-slate-50 border border-slate-100 transition-colors group">
                  <div>
                    <p className="text-xs font-medium text-slate-800">{item.label}</p>
                    <p className="text-xs text-slate-400">{item.desc}</p>
                  </div>
                  <ArrowRight size={13} className="text-slate-300 group-hover:text-indigo-700 transition-colors" />
                </Link>
              ))}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
